/**
 * Zona de captura de documentos reutilizable.
 *
 * Combina en un solo componente:
 *   - Zona de arrastrar-y-soltar (drag-and-drop) que también abre el selector de archivos al hacer clic
 *   - Botón "Tomar foto" que abre el dialog CameraCapture (solo si el navegador expone getUserMedia)
 *
 * La cámara está disponible tanto en móvil (trasera) como en laptop (frontal).
 * El componente detecta la disponibilidad de getUserMedia en el cliente y oculta
 * el botón de cámara si la API no está presente (ej. HTTP sin SSL).
 *
 * Al seleccionarse un archivo —por cualquier vía— se invoca onFileSelected(file).
 * El padre es responsable de almacenar el File y decidir cuándo enviarlo.
 */
import { useState, useRef, useEffect } from 'react';
import type { DragEvent, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CameraCapture } from './CameraCapture';
import type { DocVariant } from './CameraCapture';

interface DocumentCaptureZoneProps {
  /** Llamado con el File elegido (vía selector, arrastre o cámara). */
  onFileSelected: (file: File) => void;
  /** true cuando ya hay un archivo seleccionado — cambia el aspecto visual de la zona. */
  hasFile: boolean;
  /** Filtro de tipos de archivo para el input nativo. Por defecto acepta imágenes y PDF. */
  accept?: string;
  /** Texto guía que se muestra dentro de la zona de arrastre cuando no hay archivo. */
  dropHint?: string;
  /** Instrucción que el dialog de cámara muestra al usuario. */
  cameraLabel?: string;
  /**
   * Variante de documento para el dialog de cámara.
   * Cuando se provee, se muestra un tutorial específico antes de activar la cámara
   * y el marco guía adopta la forma del documento seleccionado.
   */
  docVariant?: DocVariant;
}

export function DocumentCaptureZone({
  onFileSelected,
  hasFile,
  accept = 'image/*,.pdf',
  dropHint = 'Arrastra tu documento aquí, o haz clic para seleccionar',
  cameraLabel,
  docVariant,
}: DocumentCaptureZoneProps) {
  const [dragging, setDragging] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detectar soporte de getUserMedia en el cliente — se oculta el botón si no está disponible
  useEffect(() => {
    setCameraAvailable(
      typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
    );
  }, []);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelected(file);
  };

  const handleCameraCapture = (file: File) => {
    setCameraOpen(false);
    onFileSelected(file);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Zona de arrastre — actúa como label del input oculto */}
      <Box
        component="label"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 160,
          border: '2px dashed',
          borderColor: dragging ? 'primary.main' : hasFile ? 'success.main' : 'divider',
          borderRadius: 2,
          p: 4,
          cursor: 'pointer',
          backgroundColor: dragging
            ? 'action.hover'
            : hasFile
            ? 'success.50'
            : 'background.paper',
          transition: 'border-color 0.2s, background-color 0.2s',
          gap: 1,
          '&:hover': { borderColor: hasFile ? 'success.main' : 'primary.light' },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={accept}
          onChange={handleFileChange}
        />

        {hasFile ? (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography variant="body1" color="success.main" fontWeight={600}>
              Documento seleccionado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Haz clic aquí para cambiar el archivo
            </Typography>
          </>
        ) : (
          <>
            <UploadFileIcon sx={{ fontSize: 48, color: 'action.disabled' }} />
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {dropHint}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              JPG, PNG o PDF — máximo 10 MB
            </Typography>
          </>
        )}
      </Box>

      {/* Separador y botón de cámara — solo si el navegador soporta getUserMedia */}
      {cameraAvailable && (
        <>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.disabled">
              o
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Stack>

          <Button
            variant="outlined"
            startIcon={<CameraAltIcon />}
            onClick={() => setCameraOpen(true)}
            fullWidth
          >
            Tomar foto con la cámara
          </Button>
        </>
      )}

      {/* Dialog de captura fotográfica */}
      <CameraCapture
        open={cameraOpen}
        onCapture={handleCameraCapture}
        onClose={() => setCameraOpen(false)}
        label={cameraLabel}
        docVariant={docVariant}
      />
    </Box>
  );
}
